import styled from 'styled-components';
import MyTable, { Column } from '../../components/MyTable';
import { useEffect, useState } from 'react';
import axios from 'axios';

interface TransactionCasesProps {
  caseResult?: any;
  filter?: any;
  isPremium?: boolean;
}

type TransactionCase = {
  id: string;
  date: string; // 거래일
  address: string;
  type: string; // 용도지역
  landArea: number;
  buildingArea: number | null;
  price: number;
  landPricePerArea: number;
  buildingPricePerArea: number | null;
  approvalDate?: string; // 사용승인일
  distance?: number;
};

const columns: Column<TransactionCase>[] = [
  { key: 'date', label: '거래일', align: 'left' },
  { key: 'address', label: '주소', align: 'left' },
  { key: 'type', label: '용도지역', align: 'left' },
  {
    key: 'landArea',
    label: '토지면적',
    align: 'right',
    render: (item) => (item.landArea ? item.landArea.toFixed(2) : ''),
    unit: '㎡',
  },
  {
    key: 'buildingArea',
    label: '건물면적',
    align: 'right',
    render: (item) => (item.buildingArea ? item.buildingArea.toFixed(2) : ''),
    unit: '㎡',
  },
  {
    key: 'price',
    label: '거래금액',
    align: 'right',
    render: (item) => (item.price ? item.price.toLocaleString() : ''),
    unit: '원',
  },
  {
    key: 'landPricePerArea',
    label: '토지면적 당 단가',
    align: 'right',
    render: (item) =>
      item.landPricePerArea ? item.landPricePerArea.toLocaleString() : '',
    unit: '원',
  },
  {
    key: 'buildingPricePerArea',
    label: '건물면적 당 단가',
    align: 'right',
    render: (item) =>
      item.buildingPricePerArea
        ? item.buildingPricePerArea.toLocaleString()
        : '',
    unit: '원',
  },
  { key: 'approvalDate', label: '사용승인일', align: 'left' },
  {
    key: 'distance',
    label: '거리',
    align: 'right',
    render: (item) => (item.distance ? item.distance.toFixed(1) : ''),
    unit: 'm',
  },
];

const periodLabelMap: Record<string, string> = {
  '3': '3개월 이내',
  '12': '1년 이내',
  '36': '3년 이내',
  '60': '5년 이내',
  '120': '10년 이내',
};

const rangeLabelMap: Record<string, string> = {
  '500': '주변 500m',
  '1000': '주변 1km',
  '3000': '주변 3km',
};

const TransactionCases = ({
  caseResult,
  filter,
  isPremium = false,
}: TransactionCasesProps) => {
  const [data, setData] = useState<TransactionCase[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const apiHost = process.env.REACT_APP_HOST_API;

  const selectionOptions = [
    caseResult?.address,
    caseResult?.name,
    periodLabelMap[filter?.period],
    rangeLabelMap[filter?.range],
  ];

  useEffect(() => {
    if (!caseResult) return;

    const fetchData = async () => {
      setIsLoading(true);

      try {
        const params = {
          address: caseResult?.address,
          category: caseResult?.name || caseResult?.category_group,
          mon: Number(filter?.period),
          limit: Number(filter?.count),
          distance: Number(filter?.range),
        };

        const response = await axios.get(`${apiHost}stat/deal-case`, {
          params,
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });

        if (response.data?.status === 'success') {
          const rawData = response.data.data.list;

          const validData = rawData.filter((item: any) => {
            // 모든 주요 필드가 null, 0, 빈 문자열이면 false 반환
            const values = [
              item.address,
              item.area,
              item.build_year,
              item.building_prc,
              item.deal_date,
              item.dealprice,
              item.land_prc,
              item.totalfloorarea,
              item.zoning,
            ];
            return values.some(
              (v) =>
                v !== null && v !== '' && !(typeof v === 'number' && v === 0),
            );
          });

          if (validData.length === 0) {
            setData([]);
            return;
          }

          const tableData: TransactionCase[] = validData.map(
            (item: any, index: number) => {
              const landArea = item.area != null ? Number(item.area) : 0;
              const buildingArea =
                item.totalfloorarea != null
                  ? Number(item.totalfloorarea)
                  : null;
              const price = item.dealprice != null ? Number(item.dealprice) : 0;
              const landPricePerArea =
                item.land_prc != null ? Number(item.land_prc) : '';
              const buildingPricePerArea =
                item.building_prc != null ? Number(item.building_prc) : '';

              return {
                id: index.toString(),
                date: item.deal_date || '',
                address: item.address || '',
                type: item.zoning || '',
                landArea,
                buildingArea,
                price,
                landPricePerArea,
                buildingPricePerArea,
                approvalDate: item.build_year || '',
                distance: item.distance != null ? Number(item.distance) : 0,
              };
            },
          );

          setData(tableData);
        } else {
          setData([]);
        }
      } catch (error) {
        console.error('실거래 사례 조회 실패', error);
        setData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [caseResult, filter]);

  return (
    <Wrap>
      <MyTable
        title="실거래 사례"
        selectionOptions={selectionOptions}
        columns={columns}
        data={data}
        avgKeys={[
          'landArea',
          'buildingArea',
          'price',
          'landPricePerArea',
          'buildingPricePerArea',
        ]}
        loading={isLoading}
        isPremium={isPremium}
      />
    </Wrap>
  );
};

export default TransactionCases;

const Wrap = styled.div`
  width: 100%;
  overflow-x: hidden;
  padding: 20px 0;
`;
